import { HStack, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'

import UserAvatar from '@/components/UserAvatar'

import Dot from './Dot'

const MAX_AVATAR_COUNT = 3
const SPOTS_LEFT_WARNING_THRESHOLD = 10

function Subtext({ text, color = 'fg.muted' }: { text: string; color?: string }) {
  return (
    <Text fontWeight="semibold" fontSize="xs" color={color}>
      {text}
    </Text>
  )
}

export default function SeriesLearnerTally({ series }: { series: Query }): ReactElement {
  const { id: userId } = useUser()

  const members = series.many(SeriesQ.getCurrentMembers())

  // If the current user has a reservation, we exclude them so they don't get confused and think someone took their spot.
  const activeReservations = series.many(
    SeriesQ.getActiveReservations({ userIdToExclude: userId, includeUser: true })
  )
  const membersAndReservations = [...members, ...activeReservations]

  const hasEnded = series(SeriesQ.isClosed()) ?? false
  const isUserHost = series(SeriesQ.isHost({ userId })) ?? false
  const maxLearners = series(SeriesQ.max_learners) ?? 0

  const spotsLeft = maxLearners - membersAndReservations.length

  if (!hasEnded && membersAndReservations.length === 0 && !isUserHost && maxLearners > 1) {
    return <Subtext text="✨ Be the first" />
  }

  return (
    <HStack spacing="6px">
      {membersAndReservations.length > 0 ? (
        <>
          <Dot display={{ base: 'none', md: 'block' }} />
          <HStack spacing="0px" flexDirection="row-reverse">
            {membersAndReservations
              .slice(0, MAX_AVATAR_COUNT)
              .reverse()
              .map((user, i, array) => {
                const id = user(UserQ.id) ?? 0
                const displayName = user(UserQ.display_name) ?? ''
                const avatarUrl = user(UserQ.avatar_url) ?? ''

                return (
                  <UserAvatar
                    key={id}
                    size="xs"
                    src={avatarUrl}
                    name={displayName}
                    progressionLevel={null}
                    ml={`${i !== (array.length - 1) ? -6 : 0}px`}
                    border={`1.5px solid ${chakraTokenToVar('bg')}`}
                  />
                )
              })}
          </HStack>
        </>
      ) : null}
      {membersAndReservations.length > MAX_AVATAR_COUNT ? (
        <Subtext text={`+${membersAndReservations.length - MAX_AVATAR_COUNT}`} />
      ) : null}
      {!hasEnded && spotsLeft < SPOTS_LEFT_WARNING_THRESHOLD ? (
        <>
          <Dot />
          {spotsLeft === 0 ? (
            <Subtext text="Registration full" />
          ) : (
            <Subtext text={`🔥 ${pluralize(spotsLeft, 'spot')} left!`} color="fg" />
          )}
        </>
      ) : null}
    </HStack>
  )
}
